import React, { useMemo, useState } from 'react';
import {
  HOW_STEPS,
  SOURCE_DEFS,
  WEEK_COUNT,
  DEFAULT_ACTIVE_SOURCES,
  buildChartSeries,
  buildDefaultRomiData,
  calculateRomiTracker,
  formatCount,
  formatMoney,
  getRomiInsight,
  getRomiChannelInsights,
} from './romiTrackerLogic';
import RevenueLeversNumberField from '../RevenueLevers/RevenueLeversNumberField';
import '../RevenueLevers/RevenueLevers.css';
import './RomiTracker.css';

const RomiTracker = () => {
  const locale = 'ru-RU';
  const [active, setActive] = useState(DEFAULT_ACTIVE_SOURCES);
  const [data, setData] = useState(buildDefaultRomiData);

  const toggleSource = (id) => {
    setActive((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const setCell = (sourceId, weekIdx, field) => (value) => {
    setData((prev) => {
      const weeks = [...(prev[sourceId] || Array.from({ length: WEEK_COUNT }, () => ({})))];
      weeks[weekIdx] = { ...weeks[weekIdx], [field]: value };
      return { ...prev, [sourceId]: weeks };
    });
  };

  const result = useMemo(() => calculateRomiTracker(active, data), [active, data]);
  const chart = useMemo(() => buildChartSeries(result.weeks), [result.weeks]);
  const trendInsight = useMemo(() => getRomiInsight(result.weeks), [result.weeks]);
  const channelInsights = useMemo(
    () => getRomiChannelInsights(active, data),
    [active, data],
  );

  const hasSelection = active.length > 0;

  return (
    <div className="rt-tool">
      <p className="rt-currency-note">{'Все суммы в тенге (₸). Расход — только на рекламу. Выручка — только с лидов, пришедших с рекламы.'}</p>
      <div className="rt-pick">
        <div className="rl-panel-title">{'Источники'}</div>
        <p className="rt-pick-hint">{'Выберите источники. Невыбранные не участвуют в расчёте.'}</p>
        <div className="rt-chips">
          {SOURCE_DEFS.map((src) => {
            const isOn = active.includes(src.id);
            return (
              <button
                key={src.id}
                type="button"
                className={`rt-chip${isOn ? ' rt-chip--on' : ''}`}
                onClick={() => toggleSource(src.id)}
              >
                {src.name}
              </button>
            );
          })}
        </div>
      </div>

      {!hasSelection ? (
        <div className="rt-empty">{'Выберите хотя бы один источник.'}</div>
      ) : (
        <>
          <div className="rt-sources">
            {SOURCE_DEFS.filter((src) => active.includes(src.id)).map((src) => (
              <div key={src.id} className="rl-panel rt-source-panel">
                <div className="rl-panel-title">{src.name}</div>
                <div className="rt-weeks-grid">
                  {Array.from({ length: WEEK_COUNT }, (_, weekIdx) => (
                    <div key={weekIdx} className="rt-week-block">
                      <div className="rt-week-label">{`Неделя ${weekIdx + 1}`}</div>
                      <RevenueLeversNumberField
                        label={'Расход на рекламу'}
                        unit={'₸'}
                        value={data[src.id]?.[weekIdx]?.spend ?? 0}
                        onChange={setCell(src.id, weekIdx, 'spend')}
                        min={0}
                      />
                      <RevenueLeversNumberField
                        label={'Лидов в месяц'}
                        unit={'шт'}
                        value={data[src.id]?.[weekIdx]?.leads ?? 0}
                        onChange={setCell(src.id, weekIdx, 'leads')}
                        min={0}
                      />
                      <RevenueLeversNumberField
                        label={'Выручка с рекламных лидов'}
                        unit={'₸'}
                        value={data[src.id]?.[weekIdx]?.revenue ?? 0}
                        onChange={setCell(src.id, weekIdx, 'revenue')}
                        min={0}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="rt-summary">
            <div className="rl-panel-title">{'Итого за 4 недели'}</div>
            <div className="rt-summary-grid">
              <div className="rt-stat">
                <span className="rt-stat-label">{'Расход на рекламу'}</span>
                <span className="rt-stat-value">{formatMoney(result.period.spend, locale)}</span>
              </div>
              <div className="rt-stat">
                <span className="rt-stat-label">{'Лиды'}</span>
                <span className="rt-stat-value">{formatCount(result.period.leads, locale)}</span>
              </div>
              <div className="rt-stat">
                <span className="rt-stat-label">{'CPL'}</span>
                <span className="rt-stat-value">
                  {result.period.cpl != null ? formatMoney(result.period.cpl, locale) : '—'}
                </span>
              </div>
              <div className="rt-stat">
                <span className="rt-stat-label">{'ROMI'}</span>
                <span
                  className={`rt-stat-value${
                    result.period.romi != null && result.period.romi >= 0 ? ' rt-romi--pos' : ' rt-romi--neg'
                  }`}
                >
                  {result.period.romi != null ? `${Math.round(result.period.romi)}%` : '—'}
                </span>
              </div>
            </div>
          </div>

          <div className="rt-insights-block">
            <div className="rl-section-title">{'Выводы по каналам'}</div>
            <ul className="rt-insights-list">
              {channelInsights.map((item, i) => (
                <li key={`${item.text}-${i}`}>{item.text}</li>
              ))}
            </ul>
            {trendInsight ? (
              <p className="rt-trend-insight">
                <strong>{'Динамика по неделям'}:</strong> {trendInsight.text}
              </p>
            ) : null}
          </div>

          <div className="rt-chart-block">
            <div className="rl-section-title">{'Динамика CPL и ROMI'}</div>
            <div className="rt-chart-legend">
              <span className="rt-legend-item">
                <span className="rt-legend-line rt-legend-line--cpl" />
                {'CPL'}
              </span>
              <span className="rt-legend-item">
                <span className="rt-legend-line rt-legend-line--romi" />
                {'ROMI'}
              </span>
            </div>
            <svg className="rt-chart" viewBox="0 0 100 48" preserveAspectRatio="none" aria-hidden>
              <polyline className="rt-line rt-line--cpl" points={chart.cplPoints} fill="none" />
              <polyline className="rt-line rt-line--romi" points={chart.romiPoints} fill="none" />
            </svg>
            <div className="rt-chart-labels">
              {result.weeks.map((w) => (
                <span key={w.week}>{`Неделя ${w.week}`}</span>
              ))}
            </div>
          </div>

          <div className="rl-section-title">{'Сценарии масштабирования'}</div>
          <div className="rl-table-wrap">
            <table className="rl-table">
              <thead>
                <tr>
                  <th>{'Неделя'}</th>
                  <th>{'Расход на рекламу'}</th>
                  <th>{'Лиды'}</th>
                  <th>{'CPL'}</th>
                  <th>{'Выручка'}</th>
                  <th>{'ROMI'}</th>
                </tr>
              </thead>
              <tbody>
                {result.weeks.map((row) => (
                  <tr key={row.week}>
                    <td>{`Неделя ${row.week}`}</td>
                    <td>{formatMoney(row.spend, locale)}</td>
                    <td>{formatCount(row.leads, locale)}</td>
                    <td>{row.cpl != null ? formatMoney(row.cpl, locale) : '—'}</td>
                    <td>{formatMoney(row.revenue, locale)}</td>
                    <td className={row.romi != null && row.romi >= 0 ? 'rt-romi--pos' : 'rt-romi--neg'}>
                      {row.romi != null ? `${Math.round(row.romi)}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="rl-how">
        <div className="rl-panel-title">{'Как считается'}</div>
        {HOW_STEPS.map((step) => (
          <div key={step.num} className="rl-how-step">
            <div className="rl-how-num">{step.num}</div>
            <div className="rl-how-text">
              <strong>{step.title}</strong> {step.text}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RomiTracker;
